/**
 * Proposal Management Dashboard
 * 
 * Unified dashboard for proposal and opportunity management:
 * - Proposal List: Browse all proposals
 * - Search: Advanced proposal search
 * - Requirements: Extract requirements from RFPs
 * - Matching: Match opportunities to capabilities
 * - Pipeline: Track opportunity stages
 * - Review: Senior review workflow
 */

import React, { useState, useEffect } from 'react';
import { Tabs } from '@tracekit/core';
import { FileText, Search, GitBranch, CheckCircle, Users } from 'lucide-react';
// TODO: Re-add ListChecks and Target icon imports when Requirements and Opportunity Matching tabs are re-enabled
import { ProposalListPage } from './ProposalList';
import { ProposalSearchPage } from './ProposalSearch';
// TODO: Re-enable RequirementsExtractorPage import when Requirements feature is complete
// import { RequirementsExtractorPage } from './RequirementsExtractorPage';
// TODO: Re-enable OpportunityMatchingPage import when Opportunity Matching feature is complete
// import { OpportunityMatchingPage } from './OpportunityMatchingPage';
import { OpportunityPipelinePage } from './OpportunityPipelinePage';
import { ReviewWorkflowPage } from './ReviewWorkflow';
import { StaffAssignmentsDashboard } from '../components/StaffAssignmentsDashboard';
import { Card, Button, Badge } from '@tracekit/core';

export function ProposalManagementDashboard() {
  // Get tab and proposal_id from URL query params
  const urlParams = new URLSearchParams(window.location.search);
  const defaultTab = urlParams.get('tab') || 'list';
  const proposalId = urlParams.get('proposal_id');
  
  // Update URL when tab changes
  const handleTabChange = (tabId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabId);
    // Preserve proposal_id if it exists
    if (proposalId) {
      url.searchParams.set('proposal_id', proposalId);
    }
    window.history.pushState({}, '', url.toString());
  };
  
  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <FileText className="h-8 w-8 text-blue-600" />
          Proposal & Opportunity Management
        </h1>
        <p className="text-gray-600 mt-1">
          Manage proposals, extract requirements, match opportunities, and track pipeline
        </p>
      </div>
      
      {/* Tabs */}
      <Tabs
        tabs={[
          {
            id: 'list',
            label: 'Current Proposals',
            icon: <FileText className="h-4 w-4" />,
            description: 'Browse all proposals',
            content: <ProposalListPage />,
          },
          {
            id: 'search',
            label: 'Past Proposal Context',
            icon: <Search className="h-4 w-4" />,
            description: 'Advanced proposal search',
            content: <ProposalSearchPage />,
          },
          {
            id: 'staff-assignments',
            label: 'Staff Assignments',
            icon: <Users className="h-4 w-4" />,
            description: 'Manage staff assignments to proposals',
            content: <StaffAssignmentsDashboard />,
          },
          // TODO: Re-enable Requirements tab when RequirementsExtractorPage feature is complete
          // {
          //   id: 'requirements',
          //   label: 'Requirements',
          //   icon: <ListChecks className="h-4 w-4" />,
          //   description: 'Extract RFP requirements',
          //   content: <RequirementsExtractorPage />,
          // },
          // TODO: Re-enable Opportunity Matching tab when OpportunityMatchingPage feature is complete
          // {
          //   id: 'matching',
          //   label: 'Opportunity Matching',
          //   icon: <Target className="h-4 w-4" />,
          //   description: 'Match to capabilities',
          //   content: <OpportunityMatchingPage />,
          // },
          {
            id: 'pipeline',
            label: 'Pipeline',
            icon: <GitBranch className="h-4 w-4" />,
            description: 'Track opportunity stages',
            content: <OpportunityPipelinePage />,
          },
          {
            id: 'review',
            label: 'Review',
            icon: <CheckCircle className="h-4 w-4" />,
            description: 'Senior review workflow',
            content: <ReviewWorkflowPage />,
          },
        ]}
        defaultTab={defaultTab}
        onChange={handleTabChange}
      />
    </div>
  );
}

